import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Bell, CheckCircle2, ClipboardCheck, Calendar, FileText, Camera, Music, Info, X } from "lucide-react";
import { meApi } from "@/lib/api";
import { formatDateTimeLong } from "@/lib/date";

const CATEGORY_ICON = {
  validation: ClipboardCheck,
  rdv: Calendar,
  document: FileText,
  photobooth: Camera,
  contrat: FileText,
  playlist: Music,
  info: Info,
};

/**
 * Cloche notification pour l'espace client — dropdown avec compteur non-lus.
 */
export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const d = await meApi.notifications(20);
      setItems(d.items || []);
      setUnread(d.unread || 0);
    } catch { /* ignore */ }
  }, []);

  const refreshCount = useCallback(async () => {
    try {
      const d = await meApi.notificationsUnread();
      setUnread(d.unread || 0);
    } catch { /* ignore */ }
  }, []);

  // Initial load + poll every 60s
  useEffect(() => {
    refreshCount();
    const t = setInterval(refreshCount, 60000);
    return () => clearInterval(t);
  }, [refreshCount]);

  // Load full list when dropdown opens
  useEffect(() => {
    if (open) { setLoading(true); refresh().finally(() => setLoading(false)); }
  }, [open, refresh]);

  const markAllRead = async () => {
    try {
      await meApi.notificationsMarkRead(null);
      await refresh();
    } catch { /* ignore */ }
  };

  const handleItemClick = async (n) => {
    if (!n.is_read) {
      try { await meApi.notificationsMarkRead([n.id]); } catch { /* ignore */ }
    }
    setOpen(false);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="notif-bell-toggle"
        className="relative inline-flex items-center justify-center w-10 h-10 border border-[#DCB457]/30 text-white/70 hover:text-[#DCB457] hover:border-[#DCB457]/60 transition rounded-sm"
        aria-label={`Notifications${unread > 0 ? ` (${unread} non-lues)` : ""}`}
      >
        <Bell size={16} strokeWidth={1.4} />
        {unread > 0 && (
          <span
            data-testid="notif-bell-count"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#DCB457] text-black text-[10px] font-bold flex items-center justify-center"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          {/* Dropdown */}
          <div
            data-testid="notif-bell-dropdown"
            className="absolute right-0 mt-2 w-[360px] max-w-[calc(100vw-2rem)] bg-black/95 backdrop-blur-xl border border-[#DCB457]/30 shadow-2xl z-50"
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#DCB457]/15">
              <p className="font-serif-luxe text-base">Notifications</p>
              <div className="flex items-center gap-3">
                {unread > 0 && (
                  <button
                    type="button"
                    onClick={markAllRead}
                    data-testid="notif-mark-all-read"
                    className="text-[10px] uppercase tracking-[0.16em] text-[#DCB457] hover:opacity-80"
                  >
                    <CheckCircle2 size={12} strokeWidth={1.5} className="inline mr-1" />
                    Tout marquer lu
                  </button>
                )}
                <button onClick={() => setOpen(false)} aria-label="Fermer" className="text-white/50 hover:text-white">
                  <X size={14} strokeWidth={1.5} />
                </button>
              </div>
            </div>

            <div className="max-h-[420px] overflow-y-auto" data-testid="notif-bell-list">
              {loading ? (
                <p className="p-6 text-white/50 text-sm italic text-center">Chargement…</p>
              ) : items.length === 0 ? (
                <p className="p-6 text-white/50 text-sm italic text-center">Aucune notification pour le moment.</p>
              ) : (
                items.map((n) => {
                  const Icon = CATEGORY_ICON[n.category] || Info;
                  const Wrapper = n.link ? Link : "div";
                  const wrapperProps = n.link ? { to: n.link, onClick: () => handleItemClick(n) } : {};
                  return (
                    <Wrapper
                      key={n.id}
                      {...wrapperProps}
                      data-testid={`notif-item-${n.id}`}
                      className={`block px-5 py-3 border-b border-[#DCB457]/10 last:border-b-0 hover:bg-white/[0.02] transition ${
                        !n.is_read ? "bg-[#DCB457]/[0.04]" : ""
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <Icon size={15} strokeWidth={1.4} className={`shrink-0 mt-0.5 ${!n.is_read ? "text-[#DCB457]" : "text-white/40"}`} />
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm leading-snug ${!n.is_read ? "text-white" : "text-white/70"}`}>{n.title}</p>
                          {n.body && <p className="text-[12px] text-white/55 leading-snug mt-1 line-clamp-2">{n.body}</p>}
                          <p className="text-[10px] uppercase tracking-[0.14em] text-white/35 mt-1.5">
                            {formatDateTimeLong(n.created_at) || n.created_at?.slice(0, 16).replace("T", " ")}
                          </p>
                        </div>
                        {!n.is_read && <span className="w-2 h-2 rounded-full bg-[#DCB457] mt-1.5 shrink-0" />}
                      </div>
                    </Wrapper>
                  );
                })
              )}
            </div>

            <div className="px-5 py-3 border-t border-[#DCB457]/15 text-center">
              <Link
                to="/espace-client/notifications"
                onClick={() => setOpen(false)}
                data-testid="notif-bell-see-all"
                className="text-[11px] uppercase tracking-[0.18em] text-white/60 hover:text-[#DCB457]"
              >
                Voir toutes les notifications
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
